'use client';

import { useEffect } from 'react';
import { Search } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';

import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { useSearchParamsContext } from '@/contexts/search-params-context';
import { cn } from '@/lib/utils';

import { Input } from '../ui/input';

type MobileSearchFormValues = {
  query: string;
};

type MobileSearchFormProps = {
  className?: string;
  onSearch: () => void;
};

export const MobileSearchForm = ({
  className,
  onSearch,
}: MobileSearchFormProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { filters } = useSearchParamsContext();

  const form = useForm<MobileSearchFormValues>({
    defaultValues: {
      query: searchParams.get('query') || '',
    },
  });

  useEffect(() => {
    const query = searchParams.get('query');
    form.setValue('query', query || '');
  }, [searchParams, form]);

  const handleSubmit = (data: MobileSearchFormValues) => {
    const query = data.query.trim();
    if (!query) {
      return;
    }

    const params = new URLSearchParams();
    params.set('query', query);

    if (filters.location) {
      params.set('location', filters.location);
    }

    if (filters.status) {
      params.set('status', filters.status);
    }

    if (filters.skills && filters.skills.length > 0) {
      params.set('skills', filters.skills.join(','));
    }

    if (filters.sort && filters.sort !== 'newest') {
      params.set('sort', filters.sort);
    }

    router.push(`/search?${params.toString()}`);
    onSearch();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className={cn('relative w-full', className)}
      >
        <FormField
          control={form.control}
          name='query'
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className='relative flex-1'>
                  <span className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'>
                    <Search className='size-5' />
                  </span>
                  <Input
                    {...field}
                    type='text'
                    placeholder='Search for anything'
                    className='w-full rounded-md bg-gray-100 py-2 pl-10 pr-2 text-sm outline-none'
                    autoFocus
                  />
                </div>
              </FormControl>
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
